import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  TextField,
  Grid,
  Paper,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  RecordVoiceOver as DialogueIcon,
  VolumeUp as AudioIcon,
  QuestionAnswer as QuestionIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";

/**
 * QuestionRenderer - Universal component for rendering different question types
 * Supports: multiple-choice, fill-blank, mix-match, short_answer, dialogue-group
 */
const QuestionRenderer = ({
  question,
  answer,
  onAnswerChange,
  showResult = false,
  disabled = false,
  questionNumber,
}) => {
  // Use ref to track if we're syncing from parent to avoid infinite loops
  const isSyncingRef = useRef(false);
  const [blankAnswers, setBlankAnswers] = useState(answer || {});

  // Sync with parent answer prop when it changes externally (only if different)
  useEffect(() => {
    if (answer && typeof answer === "object" && !isSyncingRef.current) {
      // Only update if the answer is actually different
      const isDifferent =
        JSON.stringify(answer) !== JSON.stringify(blankAnswers);
      if (isDifferent) {
        isSyncingRef.current = true;
        setBlankAnswers(answer);
        setTimeout(() => {
          isSyncingRef.current = false;
        }, 0);
      }
    }
  }, [answer]);

  // Render fill-blank question text with inline input boxes (HTML parsing)
  const renderFillBlankQuestion = (questionText) => {
    if (!questionText) return null;

    const parts = [];
    let lastIndex = 0;
    const regex = /\{\{(\d+)\}\}/g;
    let match;

    // Split text by blank markers, preserving HTML
    while ((match = regex.exec(questionText)) !== null) {
      // Add HTML text before the blank
      if (match.index > lastIndex) {
        parts.push({
          type: "html",
          content: questionText.substring(lastIndex, match.index),
        });
      }

      // Add the blank input
      parts.push({
        type: "blank",
        position: parseInt(match[1]),
      });

      lastIndex = regex.lastIndex;
    }

    // Add remaining HTML text
    if (lastIndex < questionText.length) {
      parts.push({
        type: "html",
        content: questionText.substring(lastIndex),
      });
    }

    return (
      <Box
        sx={{
          lineHeight: 2.5,
          fontSize: "1.1rem",
          // Force all child elements to be inline
          "& p": {
            display: "inline",
            margin: 0,
            padding: 0,
          },
          "& br": {
            display: "none",
          },
        }}
      >
        {parts.map((part, idx) => {
          if (part.type === "html") {
            // Render HTML content
            return (
              <span
                key={idx}
                dangerouslySetInnerHTML={{ __html: part.content }}
              />
            );
          } else {
            // Render input box for blank
            const blank = question.blanks?.find(
              (b) => b.position === part.position,
            );
            const userAnswer = blankAnswers?.[part.position] || "";
            const isCorrect =
              showResult &&
              blank?.correctAnswers?.some(
                (ans) =>
                  ans.toLowerCase().trim() === userAnswer.toLowerCase().trim(),
              );
            const isIncorrect = showResult && !isCorrect && userAnswer;

            // Get first character hint if enabled
            const showHint =
              blank?.firstCharacterHint && !showResult && !userAnswer;
            const hintText =
              showHint && blank?.correctAnswers?.[0]
                ? `${blank.correctAnswers[0].charAt(0).toUpperCase()}...`
                : `(${part.position})`;

            return (
              <TextField
                key={idx}
                value={userAnswer}
                onChange={(e) =>
                  handleFillBlankChange(part.position, e.target.value)
                }
                disabled={disabled || showResult}
                placeholder={hintText}
                size="small"
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false",
                  list: "autocompleteOff",
                  name: `q-blank-${question?._id || "x"}-${part.position}`,
                }}
                sx={{
                  display: "inline-flex",
                  verticalAlign: "middle",
                  mx: "2px",
                  // Dynamic width based on content, with min/max constraints
                  width: userAnswer
                    ? `${Math.max(
                        60,
                        Math.min(200, userAnswer.length * 12 + 30),
                      )}px`
                    : "80px",
                  maxWidth: "200px",
                  "& .MuiInputBase-root": {
                    backgroundColor: showResult
                      ? isCorrect
                        ? "#e8f5e9"
                        : isIncorrect
                        ? "#ffebee"
                        : "#f5f5f5"
                      : "white",
                    borderRadius: "4px",
                    fontSize: "1rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: showResult
                      ? isCorrect
                        ? "#4caf50 !important"
                        : isIncorrect
                        ? "#f44336 !important"
                        : "#bdbdbd !important"
                      : "#1976d2",
                  },
                  "& .MuiInputBase-input": {
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "1rem",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: disabled || showResult ? undefined : "#1565c0",
                  },
                }}
              />
            );
          }
        })}
      </Box>
    );
  };

  // Handle fill-blank answer change
  const handleFillBlankChange = (position, value) => {
    const updated = {
      ...blankAnswers,
      [position]: value,
    };
    setBlankAnswers(updated);
    // Notify parent with updated value after state update
    onAnswerChange(updated);
  };

  // Render based on question type
  const renderQuestionContent = () => {
    switch (question.type) {
      case "mix-match":
        // Mix-match shows fill-blank content but uses multiple-choice answers
        return (
          <Box sx={{ mt: 2 }}>
            {/* Display content with blanks (read-only display) */}
            {question.content && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Content:
                </Typography>
                <Box
                  sx={{
                    lineHeight: 2.5,
                    fontSize: "1.1rem",
                    "& p": { display: "inline", margin: 0, padding: 0 },
                    "& br": { display: "none" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: question.content.replace(
                      /\{\{(\d+)\}\}/g,
                      '<span style="background-color: #e3f2fd; padding: 2px 8px; border-radius: 4px; font-weight: bold; color: #1976d2;">___$1___</span>',
                    ),
                  }}
                />
              </Paper>
            )}

            {/* Render multiple-choice options for the question */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Question:
            </Typography>
            <RadioGroup
              value={answer || ""}
              onChange={(e) => onAnswerChange(e.target.value)}
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                {(
                  question.choices ||
                  question.options?.map((opt, idx) => ({
                    value: String.fromCharCode(65 + idx),
                    text: opt,
                    isCorrect:
                      question.correctAnswer === String.fromCharCode(65 + idx),
                  })) ||
                  []
                ).map((option, idx) => {
                  const optionChar =
                    option.value || String.fromCharCode(65 + idx);
                  const isSelected = answer === optionChar;
                  const isThisCorrect = option.isCorrect;
                  const showCorrect = showResult && isThisCorrect;
                  const showWrong = showResult && isSelected && !isThisCorrect;

                  return (
                    <Box
                      key={option._id || idx}
                      onClick={() =>
                        !disabled && !showResult && onAnswerChange(optionChar)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: disabled || showResult ? "default" : "pointer",
                        p: 0.5,
                        opacity: showResult ? 0.9 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          minWidth: 32,
                          minHeight: 32,
                          borderRadius: "50%",
                          bgcolor: showCorrect
                            ? "success.main"
                            : showWrong
                            ? "error.main"
                            : isSelected
                            ? "primary.main"
                            : "white",
                          border: "1px solid",
                          borderColor: showCorrect
                            ? "success.main"
                            : showWrong
                            ? "error.main"
                            : isSelected
                            ? "primary.main"
                            : "black",
                          color:
                            showCorrect || showWrong || isSelected
                              ? "white"
                              : "black",
                          mr: 2,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            bgcolor:
                              disabled || showResult
                                ? showCorrect
                                  ? "success.main"
                                  : showWrong
                                  ? "error.main"
                                  : isSelected
                                  ? "primary.main"
                                  : "white"
                                : isSelected
                                ? "primary.main"
                                : "grey.200",
                          },
                        }}
                      >
                        {optionChar}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {option.text}
                        </Typography>
                        {showCorrect && (
                          <CheckIcon color="success" fontSize="small" />
                        )}
                        {showWrong && (
                          <CloseIcon color="error" fontSize="small" />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </RadioGroup>
          </Box>
        );

      case "fill-blank":
        return (
          <Box sx={{ mt: 2 }}>
            {/* Display question with inline input boxes */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              {renderFillBlankQuestion(question.content || question.question)}
            </Paper>

            {/* Show correct answers when in result mode */}
            {showResult && question.blanks && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Correct Answers:
                </Typography>
                <Grid container spacing={1}>
                  {question.blanks.map((blank) => {
                    const userAnswer = blankAnswers?.[blank.position] || "";
                    const isCorrect = blank.correctAnswers?.some(
                      (ans) =>
                        ans.toLowerCase().trim() ===
                        userAnswer.toLowerCase().trim(),
                    );

                    return (
                      <Grid item xs={12} sm={6} md={4} key={blank.position}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: isCorrect ? "#e8f5e9" : "#ffebee",
                            border: "1px solid",
                            borderColor: isCorrect ? "#4caf50" : "#f44336",
                          }}
                        >
                          <Typography variant="caption" fontWeight={600}>
                            ({blank.position}) {isCorrect ? "✓" : "✗"}{" "}
                            {blank.correctAnswers?.join(", ")}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Box>
        );

      case "dialogue-group":
        // Dialogue-group questions are parent questions
        // They don't render themselves - only their child questions render
        // This case should not normally be reached as the parent is not displayed
        return (
          <Box sx={{ mt: 2 }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 50%)",
                  pointerEvents: "none",
                },
              }}
            >
              {/* Header with Icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.25)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  <DialogueIcon sx={{ fontSize: 32, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    Dialogue Exercise
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      fontWeight: 500,
                    }}
                  >
                    Interactive conversation practice
                  </Typography>
                </Box>
              </Box>

              {/* Info Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <InfoIcon sx={{ color: "#667eea", mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                      gutterBottom
                    >
                      About This Exercise
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      This dialogue exercise contains a conversation with
                      fill-in-the-blank sections and comprehension questions.
                    </Typography>
                  </Box>
                </Box>

                {/* Features */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<DialogueIcon />}
                    label="Conversation Practice"
                    size="small"
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      fontWeight: 600,
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                    }}
                  />
                  {question.audioUrl && (
                    <Chip
                      icon={<AudioIcon />}
                      label="Audio Available"
                      size="small"
                      sx={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        color: "white",
                        fontWeight: 600,
                        "& .MuiChip-icon": {
                          color: "white",
                        },
                      }}
                    />
                  )}
                  {question.dialogueMetadata?.speakers?.length > 0 && (
                    <Chip
                      label={`${question.dialogueMetadata.speakers.length} Speakers`}
                      size="small"
                      sx={{
                        background:
                          "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Chip
                    icon={<QuestionIcon />}
                    label="Multiple Components"
                    size="small"
                    sx={{
                      background:
                        "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                      color: "white",
                      fontWeight: 600,
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                    }}
                  />
                </Stack>

                {/* Context if available */}
                {question.dialogueMetadata?.context && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      display="block"
                      gutterBottom
                    >
                      CONTEXT:
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {question.dialogueMetadata.context}
                    </Typography>
                  </Box>
                )}

                {/* Speakers if available */}
                {question.dialogueMetadata?.speakers?.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      display="block"
                      gutterBottom
                    >
                      SPEAKERS:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {question.dialogueMetadata.speakers.map(
                        (speaker, idx) => (
                          <Chip
                            key={idx}
                            label={`${speaker.name}${
                              speaker.role ? ` - ${speaker.role}` : ""
                            }`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#667eea",
                              color: "#667eea",
                              fontWeight: 500,
                            }}
                          />
                        ),
                      )}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Box>
        );

      case "short_answer":
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Your Answer"
              placeholder="Type your answer here..."
              value={answer || ""}
              onChange={(e) => onAnswerChange(e.target.value)}
              disabled={disabled || showResult}
              variant="outlined"
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: "false",
                list: "autocompleteOff",
                name: `q-short-${question?._id || "x"}`,
              }}
            />
            {showResult && question.correctAnswer && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "info.light",
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: "info.main",
                }}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Sample Answer:
                </Typography>
                <Typography variant="body2">
                  {question.correctAnswer || ""}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case "multiple-choice":
      default:
        // Handle both formats: choices array or options array
        const choices = question.choices?.length
          ? question.choices
          : question.options?.length
          ? question.options
          : question.answerChoices?.length
          ? question.answerChoices
          : [];
        console.log("choices", choices);
        console.log("question", question);
        return (
          <RadioGroup
            value={answer || ""}
            onChange={(e) => onAnswerChange(e.target.value)}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              {choices.map((option, idx) => {
                const optionChar =
                  option.value || String.fromCharCode(65 + idx);
                const isSelected = answer === optionChar;
                const isThisCorrect = option.isCorrect;
                const showCorrect = showResult && isThisCorrect;
                const showWrong = showResult && isSelected && !isThisCorrect;

                return (
                  <Box
                    key={option._id || idx}
                    onClick={() =>
                      !disabled && !showResult && onAnswerChange(optionChar)
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: disabled || showResult ? "default" : "pointer",
                      p: 0.5,
                      opacity: showResult ? 0.9 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        borderRadius: "50%",
                        bgcolor: showCorrect
                          ? "success.main"
                          : showWrong
                          ? "error.main"
                          : isSelected
                          ? "primary.main"
                          : "white",
                        border: "1px solid",
                        borderColor: showCorrect
                          ? "success.main"
                          : showWrong
                          ? "error.main"
                          : isSelected
                          ? "primary.main"
                          : "black",
                        color:
                          showCorrect || showWrong || isSelected
                            ? "white"
                            : "black",
                        mr: 2,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          bgcolor:
                            disabled || showResult
                              ? showCorrect
                                ? "success.main"
                                : showWrong
                                ? "error.main"
                                : isSelected
                                ? "primary.main"
                                : "white"
                              : isSelected
                              ? "primary.main"
                              : "grey.200",
                        },
                      }}
                    >
                      {optionChar}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">
                        {option.text}
                      </Typography>
                      {showCorrect && (
                        <CheckIcon color="success" fontSize="small" />
                      )}
                      {showWrong && (
                        <CloseIcon color="error" fontSize="small" />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </RadioGroup>
        );
    }
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        pt: 0,
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Divider sx={{ mb: 2, borderColor: "dark-gray" }} />
      <Typography
        variant="h6"
        component="h5"
        sx={{ fontWeight: 600, color: "dark-gray" }}
      >
        Question {questionNumber || question.number || 1}
      </Typography>

      <Typography
        variant="body1"
        component="p"
        sx={{ mt: 1, mb: 2 }}
      >
        {question.question}
      </Typography>

      {renderQuestionContent()}
    </Box>
  );
};

export default QuestionRenderer;
